/**
 * Phase 2.15 — Technical Validation Findings (Reviewer Console tab).
 *
 * Reviewer-managed evidence (VAPT, OSINT, cloud review, etc.). These are
 * NOT official Findings and do not affect scoring. A reviewer can promote
 * an item into the existing assessment_findings table via the "Promote"
 * action; promoted items then appear in the Findings tab and report.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pencil,
  Trash2,
  Plus,
  Loader2,
  ShieldCheck,
  ArrowUpRight,
  RotateCcw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  TVF_CATEGORIES,
  TVF_DISCLAIMER,
  TVF_EVIDENCE_SOURCES,
  TVF_SEVERITIES,
  TVF_STATUSES,
  FINDING_RISK_LEVELS,
  FINDING_PRIORITY_LEVELS,
  FINDING_TIMEFRAMES,
  defaultsForCategory,
  severityDefaults,
  type TvfRow,
} from "@/reports/telecom/technicalValidation";

const sb: any = supabase as any;

type Draft = {
  id: string | null;
  finding_title: string;
  category: string;
  severity: string;
  affected_asset: string;
  description: string;
  business_impact: string;
  recommendation: string;
  evidence_source: string;
  status: string;
  reviewer_notes: string;
};

type PromoteDraft = {
  tvf_id: string;
  control_code: string;
  finding_title: string;
  risk_level: string;
  priority_level: string;
  timeframe: string;
  recommended_action: string;
  recommended_measures: string;
  remediation_path: string;
  reviewer_comment: string;
  csv_domain: string;
};

const EMPTY: Draft = {
  id: null,
  finding_title: "",
  category: "Other",
  severity: "Medium",
  affected_asset: "",
  description: "",
  business_impact: "",
  recommendation: "",
  evidence_source: "Manual Reviewer Entry",
  status: "Open",
  reviewer_notes: "",
};

interface Props {
  assessmentId: string;
  disabled?: boolean;
}

function severityBadgeClass(sev: string): string {
  switch (sev) {
    case "Critical":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
    case "High":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
    case "Medium":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
    case "Low":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
    case "Informational":
    default:
      return "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200";
  }
}

function promotionBadgeClass(p: string): string {
  switch (p) {
    case "Accepted Finding":
      return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
    case "Candidate Finding":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    case "Rejected":
      return "bg-zinc-100 text-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-200";
    case "Evidence Only":
    default:
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
  }
}

export default function TechnicalValidationTab({ assessmentId, disabled }: Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<TvfRow[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [promoteDraft, setPromoteDraft] = useState<PromoteDraft | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await sb.rpc(
        "reviewer_list_technical_validation_findings",
        { p_assessment_id: assessmentId },
      );
      if (error) throw error;
      setAccessDenied(false);
      setRows((data ?? []) as TvfRow[]);
    } catch (e: any) {
      const msg = String(e?.message ?? "");
      const isAuthFailure =
        /not authorized/i.test(msg) ||
        /not authenticated/i.test(msg) ||
        e?.code === "42501";
      if (isAuthFailure) {
        // Inline, non-disruptive: do not toast and do not disturb other tabs.
        setAccessDenied(true);
        setRows([]);
      } else {
        setAccessDenied(false);
        toast({
          title: "Could not load technical validation findings",
          description: msg || "Unknown error",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [assessmentId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => setDraft({ ...EMPTY });
  const startEdit = (r: TvfRow) =>
    setDraft({
      id: r.id,
      finding_title: r.finding_title ?? "",
      category: r.category ?? "Other",
      severity: r.severity ?? "Medium",
      affected_asset: r.affected_asset ?? "",
      description: r.description ?? "",
      business_impact: r.business_impact ?? "",
      recommendation: r.recommendation ?? "",
      evidence_source: r.evidence_source ?? "",
      status: r.status ?? "Open",
      reviewer_notes: r.reviewer_notes ?? "",
    });

  const saveDraft = useCallback(async () => {
    if (!draft) return;
    if (!draft.finding_title.trim()) {
      toast({
        title: "Finding title required",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const { error } = await sb.rpc(
        "reviewer_upsert_technical_validation_finding",
        {
          p_assessment_id: assessmentId,
          p_finding_id: draft.id,
          p_finding_title: draft.finding_title.trim(),
          p_category: draft.category,
          p_severity: draft.severity,
          p_affected_asset: draft.affected_asset.trim() || null,
          p_description: draft.description.trim() || null,
          p_business_impact: draft.business_impact.trim() || null,
          p_recommendation: draft.recommendation.trim() || null,
          p_evidence_source: draft.evidence_source || null,
          p_status: draft.status,
          p_reviewer_notes: draft.reviewer_notes.trim() || null,
        },
      );
      if (error) throw error;
      setDraft(null);
      await load();
      toast({ title: "Technical validation finding saved" });
    } catch (e: any) {
      toast({
        title: "Could not save",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [assessmentId, draft, load, toast]);

  const deleteRow = useCallback(
    async (id: string) => {
      if (!window.confirm("Delete this technical validation finding?")) return;
      setBusyId(id);
      try {
        const { error } = await sb.rpc(
          "reviewer_delete_technical_validation_finding",
          { p_assessment_id: assessmentId, p_finding_id: id },
        );
        if (error) throw error;
        await load();
        toast({ title: "Deleted" });
      } catch (e: any) {
        toast({
          title: "Delete failed",
          description: e?.message ?? "Unknown error",
          variant: "destructive",
        });
      } finally {
        setBusyId(null);
      }
    },
    [assessmentId, load, toast],
  );

  const setPromotionStatus = useCallback(
    async (r: TvfRow, status: string) => {
      setBusyId(r.id);
      try {
        const { error } = await sb.rpc("reviewer_set_tvf_promotion_status", {
          p_assessment_id: assessmentId,
          p_finding_id: r.id,
          p_promotion_status: status,
        });
        if (error) throw error;
        await load();
      } catch (e: any) {
        toast({
          title: "Could not update promotion status",
          description: e?.message ?? "Unknown error",
          variant: "destructive",
        });
      } finally {
        setBusyId(null);
      }
    },
    [assessmentId, load, toast],
  );

  const startPromote = (r: TvfRow) => {
    const catDef = defaultsForCategory(r.category, {
      finding_title: r.finding_title,
      affected_asset: r.affected_asset,
      evidence_source: r.evidence_source,
      description: r.description,
      recommendation: r.recommendation,
    });
    const sevDef = severityDefaults(r.severity);
    setPromoteDraft({
      tvf_id: r.id,
      control_code: catDef.control_code,
      finding_title: r.finding_title || "",
      risk_level: sevDef.risk_level,
      priority_level: sevDef.priority_level,
      timeframe: sevDef.timeframe,
      recommended_action: r.recommendation || "",
      recommended_measures: r.description || "",
      remediation_path: "",
      reviewer_comment:
        r.reviewer_notes ||
        `Promoted from Technical Validation (${r.category}). Affected: ${r.affected_asset || "—"}.`,
      csv_domain: catDef.csv_domain,
    });
  };

  const submitPromote = useCallback(async () => {
    if (!promoteDraft) return;
    if (!promoteDraft.finding_title.trim() || !promoteDraft.control_code.trim()) {
      toast({
        title: "Control and title required",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const { error } = await sb.rpc(
        "reviewer_promote_technical_validation_finding",
        {
          p_assessment_id: assessmentId,
          p_tvf_id: promoteDraft.tvf_id,
          p_control_code: promoteDraft.control_code.trim(),
          p_finding_title: promoteDraft.finding_title.trim(),
          p_risk_level: promoteDraft.risk_level,
          p_priority_level: promoteDraft.priority_level,
          p_timeframe: promoteDraft.timeframe,
          p_recommended_action: promoteDraft.recommended_action.trim() || null,
          p_recommended_measures: promoteDraft.recommended_measures.trim() || null,
          p_remediation_path: promoteDraft.remediation_path.trim() || null,
          p_reviewer_comment: promoteDraft.reviewer_comment.trim() || null,
          p_owners: [],
          p_due_date: null,
        },
      );
      if (error) throw error;
      setPromoteDraft(null);
      await load();
      toast({
        title: "Promoted into Findings tab",
        description: "Review the new Finding in the Findings tab to set owners and due date.",
      });
    } catch (e: any) {
      toast({
        title: "Promotion failed",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [assessmentId, promoteDraft, load, toast]);

  const empty = useMemo(() => !loading && rows.length === 0 && !draft, [loading, rows, draft]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          Technical Validation Findings
        </CardTitle>
        <p className="text-xs text-muted-foreground italic mt-1 max-w-4xl">
          {TVF_DISCLAIMER}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : accessDenied ? (
          <div className="rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-900 dark:text-amber-200">
            You don’t have technical-validation access on this assessment. Other Reviewer Console
            tabs remain available.
          </div>
        ) : empty ? (
          <p className="text-sm text-muted-foreground italic">
            No technical validation findings yet. Use “Add Finding” to record VAPT, OSINT,
            cloud review, or other technical observations.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b">
                  <th className="text-left py-2 pr-3">Severity</th>
                  <th className="text-left py-2 pr-3">Category</th>
                  <th className="text-left py-2 pr-3">Affected Asset</th>
                  <th className="text-left py-2 pr-3">Finding</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-left py-2 pr-3">Promotion</th>
                  <th className="text-right py-2 pr-1 w-[210px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isAccepted = r.promotion_status === "Accepted Finding";
                  return (
                    <tr key={r.id} className="border-b align-top hover:bg-muted/30">
                      <td className="py-2 pr-3">
                        <Badge className={severityBadgeClass(r.severity)} variant="secondary">
                          {r.severity}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">{r.category}</td>
                      <td className="py-2 pr-3 max-w-[200px] truncate" title={r.affected_asset ?? ""}>
                        {r.affected_asset || "—"}
                      </td>
                      <td className="py-2 pr-3 font-medium text-foreground max-w-[260px] truncate" title={r.finding_title}>
                        {r.finding_title || "—"}
                      </td>
                      <td className="py-2 pr-3">{r.status}</td>
                      <td className="py-2 pr-3">
                        <Badge className={promotionBadgeClass(r.promotion_status)} variant="secondary">
                          {r.promotion_status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-1 text-right whitespace-nowrap">
                        {!isAccepted && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 mr-1"
                            disabled={disabled || !!draft || !!promoteDraft || busyId === r.id}
                            onClick={() => startPromote(r)}
                            title="Promote to official Finding"
                          >
                            <ArrowUpRight className="h-3.5 w-3.5 mr-1" />
                            Promote
                          </Button>
                        )}
                        {r.promotion_status !== "Candidate Finding" && !isAccepted && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={disabled || busyId === r.id}
                            onClick={() => setPromotionStatus(r, "Candidate Finding")}
                            title="Mark as Candidate Finding"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          disabled={disabled || !!draft || busyId === r.id}
                          onClick={() => startEdit(r)}
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          disabled={disabled || busyId === r.id}
                          onClick={() => deleteRow(r.id)}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {draft && (
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">
              {draft.id ? "Edit Finding" : "Add Finding"}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Finding title *">
                <Input
                  value={draft.finding_title}
                  onChange={(e) => setDraft({ ...draft, finding_title: e.target.value })}
                  placeholder="e.g. Exposed admin interface on public IP"
                />
              </Field>
              <SelectField
                label="Category"
                value={draft.category}
                onChange={(v) => setDraft({ ...draft, category: v })}
                options={TVF_CATEGORIES as unknown as string[]}
              />
              <SelectField
                label="Severity"
                value={draft.severity}
                onChange={(v) => setDraft({ ...draft, severity: v })}
                options={TVF_SEVERITIES as unknown as string[]}
              />
              <Field label="Affected asset / URL / system">
                <Input
                  value={draft.affected_asset}
                  onChange={(e) => setDraft({ ...draft, affected_asset: e.target.value })}
                  placeholder="e.g. https://admin.example.com"
                />
              </Field>
              <SelectField
                label="Evidence source"
                value={draft.evidence_source}
                onChange={(v) => setDraft({ ...draft, evidence_source: v })}
                options={TVF_EVIDENCE_SOURCES}
              />
              <SelectField
                label="Status"
                value={draft.status}
                onChange={(v) => setDraft({ ...draft, status: v })}
                options={TVF_STATUSES as unknown as string[]}
              />
            </div>
            <Field label="Description">
              <Textarea
                rows={3}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Technical detail of the observation."
              />
            </Field>
            <Field label="Business impact">
              <Textarea
                rows={2}
                value={draft.business_impact}
                onChange={(e) => setDraft({ ...draft, business_impact: e.target.value })}
                placeholder="What could happen if this is exploited?"
              />
            </Field>
            <Field label="Recommendation">
              <Textarea
                rows={2}
                value={draft.recommendation}
                onChange={(e) => setDraft({ ...draft, recommendation: e.target.value })}
                placeholder="Suggested remediation."
              />
            </Field>
            <Field label="Reviewer notes">
              <Textarea
                rows={2}
                value={draft.reviewer_notes}
                onChange={(e) => setDraft({ ...draft, reviewer_notes: e.target.value })}
                placeholder="Internal notes."
              />
            </Field>
            <div className="flex gap-2">
              <Button type="button" size="sm" disabled={disabled || saving} onClick={saveDraft}>
                {saving ? "Saving…" : draft.id ? "Update" : "Add"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => setDraft(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {promoteDraft && (
          <div className="rounded-md border border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-900/10 p-3 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              Promote to Official Finding
            </div>
            <p className="text-[11px] text-muted-foreground">
              Risk, priority and timeframe use the same dropdowns as every other Finding.
              Control code defaults from category but is editable. After promotion the
              row appears in the Findings tab and is edited there like any other Finding.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Control code *">
                <Input
                  value={promoteDraft.control_code}
                  onChange={(e) => setPromoteDraft({ ...promoteDraft, control_code: e.target.value })}
                  placeholder="e.g. DE.CM-08"
                />
              </Field>
              <Field label="CSV Domain (informational)">
                <Input
                  value={promoteDraft.csv_domain}
                  onChange={(e) => setPromoteDraft({ ...promoteDraft, csv_domain: e.target.value })}
                />
              </Field>
              <Field label="Finding title *">
                <Input
                  value={promoteDraft.finding_title}
                  onChange={(e) => setPromoteDraft({ ...promoteDraft, finding_title: e.target.value })}
                />
              </Field>
              <SelectField
                label="Risk level"
                value={promoteDraft.risk_level}
                onChange={(v) => setPromoteDraft({ ...promoteDraft, risk_level: v })}
                options={FINDING_RISK_LEVELS as unknown as string[]}
              />
              <SelectField
                label="Priority"
                value={promoteDraft.priority_level}
                onChange={(v) => setPromoteDraft({ ...promoteDraft, priority_level: v })}
                options={FINDING_PRIORITY_LEVELS as unknown as string[]}
              />
              <SelectField
                label="Timeframe"
                value={promoteDraft.timeframe}
                onChange={(v) => setPromoteDraft({ ...promoteDraft, timeframe: v })}
                options={FINDING_TIMEFRAMES as unknown as string[]}
              />
            </div>
            <Field label="Recommended action">
              <Textarea
                rows={2}
                value={promoteDraft.recommended_action}
                onChange={(e) => setPromoteDraft({ ...promoteDraft, recommended_action: e.target.value })}
              />
            </Field>
            <Field label="Recommended measures">
              <Textarea
                rows={2}
                value={promoteDraft.recommended_measures}
                onChange={(e) => setPromoteDraft({ ...promoteDraft, recommended_measures: e.target.value })}
              />
            </Field>
            <Field label="Remediation path">
              <Textarea
                rows={2}
                value={promoteDraft.remediation_path}
                onChange={(e) => setPromoteDraft({ ...promoteDraft, remediation_path: e.target.value })}
              />
            </Field>
            <Field label="Reviewer comment">
              <Textarea
                rows={2}
                value={promoteDraft.reviewer_comment}
                onChange={(e) => setPromoteDraft({ ...promoteDraft, reviewer_comment: e.target.value })}
              />
            </Field>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                disabled={disabled || saving}
                onClick={submitPromote}
              >
                {saving ? "Promoting…" : "Promote to Findings"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => setPromoteDraft(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!draft && !promoteDraft && (
          <div className="pt-2 border-t">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              onClick={startAdd}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Finding
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <Field label={label}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
